import type { Cart, CartItem, GiftcardCartItem } from './types/cart'
import { useFetchPricings } from './useEventPricings'

export const useInitializePricingsForLegacyCart = (cart: Ref<Cart>) => {
  const alreadyInitialized = useState(
    'useInitializePricingsForLegacyCart-alreadyInitialized',
    () => false
  )
  const { pricingsFactory } = usePricingsFactory()

  const handleValueBasedGiftcardPricings = (items: CartItem[]) => {
    items
      .filter(
        (item) =>
          !item.pricings &&
          item.price &&
          item.type === 'GIFT_CARD' &&
          (item as GiftcardCartItem).giftcardType === 'VALUE'
      )
      .forEach((item) => {
        item.pricings = pricingsFactory(Number(item.price!.amount), {
          totalParticipants: item.quantity,
        })
        delete item.price
      })
  }

  const handleOtherItemsPricings = async (items: CartItem[]) => {
    const promises = items
      .filter((item) => !item.pricings && item.eventDescriptionId)
      .map((item) => {
        // special giftcard events are type event so do not have dateId
        const dateId = item.type === 'EVENT' ? item.id : undefined
        return useFetchPricings(ref(item.eventDescriptionId), {
          dateId: ref(dateId),
          quantity: ref(item.quantity),
          isPrivate: ref(item.private),
          type: item.type === 'GIFT_CARD' ? ref('GIFT_CARD') : undefined,
        })
          .then((pricings) => {
            item.pricings = pricings
            delete item.price
          })
          .catch((error) => {
            console.error(error)
          })
      })

    await Promise.all(promises)
  }

  const initializePricings = async () => {
    if (alreadyInitialized.value) return
    alreadyInitialized.value = true

    const items = cart.value.items

    // Dealing with value based giftcards first
    handleValueBasedGiftcardPricings(items)

    // Dealing with other items
    await handleOtherItemsPricings(items)

    // Forcing a re-render
    cart.value = {
      ...cart.value,
      items: [...items],
    }

    await nextTick()
  }

  return {
    initializePricings,
  }
}
